// Las lecturas de Clientes del portal: la cartera paginada (`GET /portal/customers`) y la ficha de
// un cliente (`GET /portal/customers/{id}`).
import { useMemo } from 'react';
import {
    keepPreviousData,
    useInfiniteQuery,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import { ApiError } from '@/api/errors';
import { wizardApi } from '@/api/services/retail-wizard';
import type {
    PortalCustomerDetail,
    PortalCustomerRow,
    PortalCustomersQuery,
} from '@/api/services/retail-wizard/contract';
import { mustRestartCustomers } from '../lib/portalCustomersCopy';

/** Clientes por página del servidor, los mismos que Admin SK · Clientes. */
export const PORTAL_CUSTOMERS_PAGE_SIZE = 25;

const STALE_TIME_MS = 60_000;

/** 404 si no le compró al vendedor; 403 por si el silo lo acota por permiso. */
const NOT_FOUND_STATUSES = new Set([403, 404]);

const NO_ROWS: PortalCustomerRow[] = [];

const PORTAL_CUSTOMERS_KEY = ['sales', 'portal-customers'] as const;

export interface PortalCustomersResult {
    /** Las páginas aplanadas, con referencia estable entre renders. */
    rows: PortalCustomerRow[];
    /** Clientes cargados cuyo nivel SK no se pudo leer. */
    creditUnavailableCount: number;
    /** La primera página de ESTA búsqueda todavía viaja. */
    isPending: boolean;
    /** Falla de la primera página: sin nada cargado no hay tabla. */
    error: unknown;
    retry: () => void;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    /** Por qué falló la página siguiente; lo cargado se queda. */
    nextPageError: unknown;
    /** El cursor venció: la lista se vuelve a pedir desde la primera página. */
    mustRestart: boolean;
    restart: () => void;
}

export function usePortalCustomers(query: Pick<PortalCustomersQuery, 'q'>): PortalCustomersResult {
    const queryClient = useQueryClient();
    const queryKey = [...PORTAL_CUSTOMERS_KEY, 'list', query] as const;
    const result = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            wizardApi.listPortalCustomers({
                ...query,
                limit: PORTAL_CUSTOMERS_PAGE_SIZE,
                ...(pageParam === undefined ? {} : { nextToken: pageParam }),
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken ?? undefined,
        staleTime: STALE_TIME_MS,
        // Al cambiar la búsqueda la tabla anterior se queda hasta que llega la nueva.
        placeholderData: keepPreviousData,
    });

    // Memo obligatorio: TanStack Table compara `data` por referencia.
    const rows = useMemo(
        () => result.data?.pages.flatMap((page) => page.items) ?? NO_ROWS,
        [result.data],
    );

    const nextPageError = result.isFetchNextPageError ? result.error : null;
    const creditUnavailableCount =
        result.data?.pages.reduce((sum, page) => sum + page.creditUnavailableCount, 0) ?? 0;

    return {
        rows,
        creditUnavailableCount,
        isPending: result.isPending,
        error: result.data ? null : result.error,
        retry: () => {
            void result.refetch();
        },
        hasNextPage: result.hasNextPage,
        isFetchingNextPage: result.isFetchingNextPage,
        fetchNextPage: () => {
            void result.fetchNextPage();
        },
        nextPageError,
        mustRestart: mustRestartCustomers(nextPageError),
        restart: () => {
            void queryClient.resetQueries({ queryKey, exact: true });
        },
    };
}

export interface PortalCustomerResult {
    detail: PortalCustomerDetail | null;
    isLoading: boolean;
    /** El silo dijo que el cliente no es de la cartera del vendedor. */
    notFound: boolean;
    /** La lectura falló por otra causa (5xx, red): se puede reintentar. */
    failed: boolean;
    retry: () => void;
}

/** `retailCustomerId` en `null` apaga la lectura. */
export function usePortalCustomer(retailCustomerId: string | null): PortalCustomerResult {
    const query = useQuery({
        queryKey: [...PORTAL_CUSTOMERS_KEY, 'detail', retailCustomerId] as const,
        queryFn: () => wizardApi.getPortalCustomer(retailCustomerId ?? ''),
        enabled: retailCustomerId !== null,
        staleTime: STALE_TIME_MS,
        retry: false,
    });
    const active = retailCustomerId !== null;
    const isNotFound =
        query.error instanceof ApiError && NOT_FOUND_STATUSES.has(query.error.httpStatus);
    return {
        detail: active ? (query.data ?? null) : null,
        isLoading: active && query.isPending && query.fetchStatus !== 'idle',
        notFound: active && query.isError && isNotFound,
        failed: active && query.isError && !isNotFound,
        retry: () => {
            void query.refetch();
        },
    };
}
